// =============================================================================
// SIREPPAR — Pestaña "Resumen Financiero" del detalle de expediente
// Solo aparece para tipos RENDICION_ANUAL y RENDICION_CAMPANA.
// Carga la rendición vinculada y muestra:
//  - Datos generales + versión actual + hash
//  - Validaciones automáticas pre-checkeadas
//  - Resumen contable (varía según tipo y tipoPresentacion)
//  - Listado de aportantes (si aplica)
//  - Checklist manual de recomendaciones para el analista (sin persistir)
// =============================================================================

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, expedienteService, rendicionAnualService, rendicionCampanaService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const ROLES_ANALISTA = ['ADMINISTRADOR', 'SUPERVISOR', 'ANALISTA'];
// El Secretario Electoral (AUTORIDAD_FIRMANTE) y el SUPERVISOR son EQUIVALENTES:
// hacen lo mismo y cubren todo el circuito. Ver ROLES_HEREDADOS en el backend
// (middleware/auth.ts), que es el gate real; esto sólo decide qué se dibuja.
const ROLES_SECRETARIO = ['ADMINISTRADOR', 'AUTORIDAD_FIRMANTE', 'SUPERVISOR'];

interface ExpedienteLite {
  id: string;
  tipo: string;
  estado: string;
  partido?: { id: string } | null;
  datosFormulario?: {
    rendicionAnualId?: string;
    rendicionCampanaId?: string;
    [k: string]: any;
  } | null;
}

interface Props {
  expediente: ExpedienteLite;
  onObservar?: () => void;
}

function formatImporte(n: number | string | null | undefined): string {
  return Number(n ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(f: string | null | undefined): string {
  if (!f) return '—';
  return new Date(f).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ResumenFinancieroExpediente({ expediente, onObservar }: Props) {
  const esAnual = expediente.tipo === 'RENDICION_ANUAL';
  const esCampana = expediente.tipo === 'RENDICION_CAMPANA';

  if (!esAnual && !esCampana) {
    return null;
  }

  const rendicionId = esAnual
    ? expediente.datosFormulario?.rendicionAnualId
    : expediente.datosFormulario?.rendicionCampanaId;

  if (!rendicionId) {
    return (
      <div className="bg-amber-50 border-l-4 border-amber-400 p-4 text-sm text-amber-900 rounded">
        El expediente no tiene rendición vinculada en sus datos. Verificá que la presentación se completó
        correctamente.
      </div>
    );
  }

  const partidoId = expediente.partido?.id ?? '';

  return (
    <div className="space-y-4">
      <AccionesAnalista expediente={expediente} onObservar={onObservar} />
      {esAnual ? (
        <ResumenAnual rendicionId={rendicionId} partidoId={partidoId} />
      ) : (
        <ResumenCampana rendicionId={rendicionId} partidoId={partidoId} />
      )}
    </div>
  );
}

// =============================================================================
// Bloque de acciones del analista (Observar / Elevar al Secretario)
// =============================================================================

function AccionesAnalista({
  expediente,
  onObservar,
}: {
  expediente: ExpedienteLite;
  onObservar?: () => void;
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const esAnalista = ROLES_ANALISTA.includes(user?.rol || '');
  const esSecretario = ROLES_SECRETARIO.includes(user?.rol || '');

  const [modalAccion, setModalAccion] = useState<'elevar' | 'aprobar' | 'rechazar' | null>(null);
  const [informe, setInforme] = useState('');
  const [resolucionNumero, setResolucionNumero] = useState('');
  const [resolucionTexto, setResolucionTexto] = useState('');
  const [motivoRechazo, setMotivoRechazo] = useState('');

  const cerrarModal = () => {
    setModalAccion(null);
    setInforme('');
    setResolucionNumero('');
    setResolucionTexto('');
    setMotivoRechazo('');
  };

  const iniciarAnalisisMutation = useMutation({
    mutationFn: () => api.post(`/expedientes/${expediente.id}/iniciar-analisis`),
    onSuccess: () => {
      toast.success('Análisis iniciado');
      queryClient.invalidateQueries({ queryKey: ['expediente', expediente.id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message ?? 'Error al iniciar análisis'),
  });

  const elevarMutation = useMutation({
    mutationFn: () => api.post(`/expedientes/${expediente.id}/enviar-secretario`, { informe }),
    onSuccess: () => {
      toast.success('Elevado al Secretario Electoral');
      queryClient.invalidateQueries({ queryKey: ['expediente', expediente.id] });
      cerrarModal();
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message ?? 'Error al elevar'),
  });

  const aprobarMutation = useMutation({
    mutationFn: () =>
      expedienteService.aprobar(expediente.id, { resolucionNumero, resolucionTexto }),
    onSuccess: () => {
      toast.success('Expediente aprobado y notificado al partido');
      queryClient.invalidateQueries({ queryKey: ['expediente', expediente.id] });
      cerrarModal();
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message ?? 'Error al aprobar'),
  });

  const rechazarMutation = useMutation({
    mutationFn: () =>
      expedienteService.rechazar(expediente.id, { motivo: motivoRechazo, resolucionNumero }),
    onSuccess: () => {
      toast.success('Expediente rechazado y notificado al partido');
      queryClient.invalidateQueries({ queryKey: ['expediente', expediente.id] });
      cerrarModal();
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message ?? 'Error al rechazar'),
  });

  const devolverAnalistaMutation = useMutation({
    mutationFn: () => api.post(`/expedientes/${expediente.id}/volver-analisis`),
    onSuccess: () => {
      toast.success('Expediente devuelto al analista');
      queryClient.invalidateQueries({ queryKey: ['expediente', expediente.id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error?.message ?? 'Error al devolver'),
  });

  if (!esAnalista && !esSecretario) return null;

  const puedeIniciar = esAnalista && expediente.estado === 'PRESENTADO';
  const puedeObservarOElevar = esAnalista && expediente.estado === 'EN_ANALISIS';
  const puedeResolver = esSecretario && ['EN_SECRETARIO', 'PENDIENTE_RESOLUCION'].includes(expediente.estado);

  const tieneAccion = puedeIniciar || puedeObservarOElevar || puedeResolver;

  if (!tieneAccion) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <strong>Estado actual: {expediente.estado}</strong> — sin acciones disponibles para tu rol.
        {['EN_SECRETARIO', 'PENDIENTE_RESOLUCION'].includes(expediente.estado) && esAnalista && (
          <span className="block text-xs mt-1">El expediente está esperando resolución del Secretario Electoral.</span>
        )}
        {expediente.estado === 'OBSERVADO' && (
          <span className="block text-xs mt-1">Esperando que el partido subsane las observaciones y re-presente.</span>
        )}
        {(expediente.estado === 'APROBADO' || expediente.estado === 'RECHAZADO') && (
          <span className="block text-xs mt-1">El expediente ya fue resuelto.</span>
        )}
      </div>
    );
  }

  const tituloPanel = puedeResolver ? 'Resolución del Secretario Electoral' : 'Acciones del analista';
  const subtituloPanel = puedeIniciar
    ? 'Tomá el expediente para iniciar el análisis.'
    : puedeObservarOElevar
    ? 'Después de revisar, observá al partido o elevá al Secretario Electoral con tu recomendación.'
    : 'Resolvé el expediente con número de resolución, o devolvelo al analista para más revisión.';

  return (
    <>
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{tituloPanel}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{subtituloPanel}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {puedeIniciar && (
              <button
                type="button"
                onClick={() => iniciarAnalisisMutation.mutate()}
                disabled={iniciarAnalisisMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {iniciarAnalisisMutation.isPending ? 'Iniciando...' : 'Iniciar análisis'}
              </button>
            )}
            {puedeObservarOElevar && (
              <>
                <button
                  type="button"
                  onClick={() => onObservar?.()}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
                  title="Ir a la pestaña Observaciones para redactar y enviar"
                >
                  Observar
                </button>
                <button
                  type="button"
                  onClick={() => setModalAccion('elevar')}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  Elevar al Secretario
                </button>
              </>
            )}
            {puedeResolver && (
              <>
                <button
                  type="button"
                  onClick={() => devolverAnalistaMutation.mutate()}
                  disabled={devolverAnalistaMutation.isPending}
                  className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  Devolver al analista
                </button>
                <button
                  type="button"
                  onClick={() => setModalAccion('rechazar')}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  onClick={() => setModalAccion('aprobar')}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
                >
                  Aprobar
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal Elevar */}
      {modalAccion === 'elevar' && (
        <ModalAccion titulo="Elevar al Secretario Electoral" onClose={cerrarModal}>
          <p className="text-sm text-gray-600 mb-3">
            Una vez elevado, el expediente pasa a <strong>EN SECRETARIO</strong>. El Secretario
            podrá aprobar, rechazar o devolver el expediente al analista.
          </p>
          <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
            Informe / recomendación del analista
          </label>
          <textarea
            value={informe}
            onChange={(e) => setInforme(e.target.value)}
            rows={8}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Resumí lo verificado y dejá tu recomendación (aprobar / rechazar / observaciones complementarias)..."
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={cerrarModal}
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => elevarMutation.mutate()}
              disabled={!informe.trim() || elevarMutation.isPending}
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {elevarMutation.isPending ? 'Elevando...' : 'Elevar al Secretario'}
            </button>
          </div>
        </ModalAccion>
      )}

      {/* Modal Aprobar */}
      {modalAccion === 'aprobar' && (
        <ModalAccion titulo="Aprobar rendición" onClose={cerrarModal}>
          <p className="text-sm text-gray-600 mb-3">
            Al aprobar se notifica al partido por mail (apoderado + domicilio electrónico) y la
            rendición pasa a estado <strong>APROBADA</strong>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
                Número de resolución <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={resolucionNumero}
                onChange={(e) => setResolucionNumero(e.target.value)}
                placeholder="Ej: 123/2026"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
            Texto de la resolución <span className="text-red-600">*</span>
          </label>
          <textarea
            value={resolucionTexto}
            onChange={(e) => setResolucionTexto(e.target.value)}
            rows={6}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Fundamentos de la resolución..."
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={cerrarModal}
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => aprobarMutation.mutate()}
              disabled={
                !resolucionNumero.trim() || !resolucionTexto.trim() || aprobarMutation.isPending
              }
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {aprobarMutation.isPending ? 'Aprobando...' : 'Aprobar rendición'}
            </button>
          </div>
        </ModalAccion>
      )}

      {/* Modal Rechazar */}
      {modalAccion === 'rechazar' && (
        <ModalAccion titulo="Rechazar rendición" onClose={cerrarModal}>
          <p className="text-sm text-gray-600 mb-3">
            Al rechazar se notifica al partido y la rendición pasa a estado{' '}
            <strong>DESAPROBADA</strong>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
                Número de resolución <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={resolucionNumero}
                onChange={(e) => setResolucionNumero(e.target.value)}
                placeholder="Ej: 124/2026"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <label className="block text-xs font-semibold uppercase text-gray-600 mb-1">
            Motivo del rechazo <span className="text-red-600">*</span>
          </label>
          <textarea
            value={motivoRechazo}
            onChange={(e) => setMotivoRechazo(e.target.value)}
            rows={6}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Fundamentos del rechazo..."
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={cerrarModal}
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => rechazarMutation.mutate()}
              disabled={
                !resolucionNumero.trim() || !motivoRechazo.trim() || rechazarMutation.isPending
              }
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
            >
              {rechazarMutation.isPending ? 'Rechazando...' : 'Rechazar rendición'}
            </button>
          </div>
        </ModalAccion>
      )}
    </>
  );
}

function ModalAccion({
  titulo,
  onClose,
  children,
}: {
  titulo: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// =============================================================================
// Validaciones automáticas (pre-checkeadas por el sistema)
// =============================================================================

interface ValidacionItem {
  codigo: string;
  titulo: string;
  ok: boolean;
  detalle?: string;
}

function ValidacionesAutomaticas({
  validaciones,
  loading,
}: {
  validaciones: ValidacionItem[] | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">Cargando validaciones...</div>
    );
  }
  if (!validaciones || validaciones.length === 0) {
    return null;
  }
  const cantOk = validaciones.filter((v) => v.ok).length;
  const cantTotal = validaciones.length;
  const todasOk = cantOk === cantTotal;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Validaciones automáticas
          <span className="text-xs text-gray-500 font-normal ml-2">(pre-checkeadas por el sistema)</span>
        </h3>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
            todasOk ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {todasOk ? `✓ ${cantOk}/${cantTotal} OK` : `✗ ${cantOk}/${cantTotal} OK`}
        </span>
      </div>
      <ul className="space-y-2">
        {validaciones.map((v) => (
          <li
            key={v.codigo}
            className={`flex items-start gap-3 p-2 rounded ${v.ok ? 'bg-green-50' : 'bg-red-50'}`}
          >
            <span
              className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                v.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
              }`}
            >
              {v.ok ? '✓' : '✗'}
            </span>
            <div className="flex-1 text-sm">
              <div className={v.ok ? 'text-gray-700' : 'text-red-900 font-medium'}>{v.titulo}</div>
              {v.detalle && (
                <div className={`text-xs ${v.ok ? 'text-gray-500' : 'text-red-700'} font-mono mt-0.5`}>
                  {v.detalle}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// Checklist manual de recomendaciones (visual, no persiste)
// =============================================================================

interface ItemChecklistManual {
  texto: string;
  detalle?: string;
}

function ChecklistManual({
  items,
  titulo = 'Recomendaciones de revisión manual',
}: {
  items: ItemChecklistManual[];
  titulo?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="border-b border-gray-200 pb-3 mb-4">
        <h3 className="text-base font-semibold text-gray-900">{titulo}</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Estos puntos no se persisten — son recordatorios visuales para el análisis.
        </p>
      </div>
      <ul className="space-y-2 text-sm">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-gray-400 mt-0.5">—</span>
            <div className="flex-1">
              <div className="text-gray-800">{item.texto}</div>
              {item.detalle && <div className="text-xs text-gray-500 mt-0.5">{item.detalle}</div>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// =============================================================================
// Header común con datos generales de la rendición
// =============================================================================

interface RendicionLite {
  id: string;
  estado: string;
  versionActual: number;
  hashActual: string | null;
  tipoPresentacion: 'COMPLETA' | 'COPIA_DISTRITO';
  fechaPresentacion: string | null;
  fechaAprobacion: string | null;
  codigoSpeca?: string | null;
  versionSpeca?: number | null;
  partido?: {
    denominacion: string;
    sigla: string | null;
  };
}

function HeaderRendicion({
  rendicion,
  subtitulo,
  wizardLink,
}: {
  rendicion: RendicionLite;
  subtitulo: string;
  wizardLink?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{subtitulo}</h3>
          {rendicion.partido && (
            <p className="text-sm text-gray-600 mt-0.5">
              {rendicion.partido.denominacion}
              {rendicion.partido.sigla ? ` (${rendicion.partido.sigla})` : ''}
            </p>
          )}
        </div>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
            rendicion.estado === 'APROBADA'
              ? 'bg-green-100 text-green-800'
              : rendicion.estado === 'DESAPROBADA'
              ? 'bg-red-100 text-red-800'
              : rendicion.estado === 'OBSERVADA'
              ? 'bg-amber-100 text-amber-900'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {rendicion.estado}
        </span>
      </div>
      <div className={`grid grid-cols-1 md:grid-cols-${rendicion.tipoPresentacion === 'COPIA_DISTRITO' ? '4' : '3'} gap-3 text-sm`}>
        <div>
          <div className="text-xs text-gray-500 uppercase">Versión actual</div>
          <div className="font-semibold">N° {rendicion.versionActual}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Tipo presentación</div>
          <div className="font-semibold">
            {rendicion.tipoPresentacion === 'COMPLETA' ? 'Completa' : 'Copia distrito'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase">Presentada</div>
          <div className="font-semibold">{formatFecha(rendicion.fechaPresentacion)}</div>
        </div>
        {rendicion.tipoPresentacion === 'COPIA_DISTRITO' && (
          <div>
            <div className="text-xs text-gray-500 uppercase">Código SPECA</div>
            <div className="font-mono text-xs break-all">{rendicion.codigoSpeca || '—'}</div>
          </div>
        )}
      </div>
      {wizardLink && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
          <Link
            to={wizardLink}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Abrir en wizard →
          </Link>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Aportantes (compartido entre anual y campaña)
// =============================================================================

interface AporteRow {
  apellido: string;
  nombre: string | null;
  razonSocial: string | null;
  dni: string | null;
  cuit: string;
  tipoPersona: 'HUMANA' | 'JURIDICA';
  modalidad: string;
  fecha: string;
  importe: number;
}

function ListadoAportantes({ aportes }: { aportes: AporteRow[] }) {
  if (aportes.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          Aportantes privados
        </h3>
        <p className="text-sm text-gray-500 italic">Sin aportes registrados.</p>
      </div>
    );
  }
  const total = aportes.reduce((acc, a) => acc + Number(a.importe), 0);
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Aportantes privados <span className="text-xs text-gray-500 font-normal">({aportes.length})</span>
        </h3>
        <div className="text-sm">
          <span className="text-gray-500">Total: </span>
          <span className="font-semibold">$ {formatImporte(total)}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Aportante</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">DNI/CUIT</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modalidad</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {aportes.map((a, i) => (
              <tr key={i}>
                <td className="px-3 py-2">
                  {a.tipoPersona === 'JURIDICA'
                    ? a.razonSocial
                    : `${a.apellido}${a.nombre ? ', ' + a.nombre : ''}`}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{a.dni || a.cuit}</td>
                <td className="px-3 py-2 text-xs">{a.modalidad}</td>
                <td className="px-3 py-2 text-xs">{formatFecha(a.fecha)}</td>
                <td className="px-3 py-2 text-right font-semibold">$ {formatImporte(a.importe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Vista Rendición Anual
// =============================================================================

interface RendicionAnualFull extends RendicionLite {
  ejercicioNumero: number;
  fechaInicio: string;
  fechaCierre: string;
  expedienteId: string | null;
  partidoId?: string;
}

function ResumenAnual({ rendicionId, partidoId }: { rendicionId: string; partidoId: string }) {
  const { data: rendicion, isLoading } = useQuery({
    queryKey: ['rendicion-anual-resumen', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.getById(rendicionId);
      return res.data as RendicionAnualFull;
    },
  });

  const { data: validacionesData } = useQuery({
    queryKey: ['rendicion-validaciones-resumen', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.getValidaciones(rendicionId);
      return res.data as { validaciones: ValidacionItem[] };
    },
    enabled: rendicion?.tipoPresentacion === 'COMPLETA',
  });

  const { data: reportes } = useQuery({
    queryKey: ['rendicion-reportes-resumen', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.getReportes(rendicionId);
      return res.data as any;
    },
    enabled: rendicion?.tipoPresentacion === 'COMPLETA',
  });

  if (isLoading || !rendicion) {
    return <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">Cargando rendición...</div>;
  }

  const esCopia = rendicion.tipoPresentacion === 'COPIA_DISTRITO';

  return (
    <div className="space-y-4">
      <HeaderRendicion
        rendicion={rendicion}
        subtitulo={`Rendición Anual — Ejercicio N° ${rendicion.ejercicioNumero} · ${formatFecha(
          rendicion.fechaInicio,
        )} al ${formatFecha(rendicion.fechaCierre)}`}
        wizardLink={`/financiamiento/rendicion-anual/${partidoId}?rendicion=${rendicionId}`}
      />

      {!esCopia && (
        <>
          <ValidacionesAutomaticas validaciones={validacionesData?.validaciones} loading={!validacionesData} />
          {reportes && <ResumenContableAnual reportes={reportes} />}
          {reportes?.aportes && <ListadoAportantes aportes={reportes.aportes ?? []} />}
        </>
      )}

      <ChecklistManual
        items={
          esCopia
            ? [
                {
                  texto: 'Verificar que el PDF de copia SPECA esté completo y legible',
                  detalle: 'Sin páginas faltantes, escaneo claro, todas las firmas visibles.',
                },
                {
                  texto: 'Verificar que el código SPECA del Paso 1 coincida con el PDF',
                  detalle: 'El código consignado en la cabecera del PDF debe coincidir con el cargado.',
                },
              ]
            : [
                {
                  texto: 'PDF certificado por CPCECh adjunto y legible',
                  detalle: 'Sello del Consejo Profesional + firma de contador matriculado.',
                },
                {
                  texto: 'PDF firmado por contador + presidente + tesorero',
                  detalle: 'Las tres firmas deben estar presentes y legibles.',
                },
                {
                  texto: 'Aportes privados razonables (montos, frecuencia, aportantes)',
                  detalle: 'Verificar que no haya picos sospechosos o aportantes recurrentes con altos montos.',
                },
              ]
        }
      />
    </div>
  );
}

function ResumenContableAnual({ reportes }: { reportes: any }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
        Resumen contable
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
        <SaldoCard
          label="Activo"
          monto={reportes.situacionPatrimonial?.activo?.total ?? 0}
          color="text-blue-700"
        />
        <SaldoCard
          label="Pasivo"
          monto={reportes.situacionPatrimonial?.pasivo?.total ?? 0}
          color="text-red-700"
        />
        <SaldoCard
          label="Patrimonio Neto"
          monto={reportes.situacionPatrimonial?.patrimonioNeto?.total ?? 0}
          color="text-green-700"
        />
        <SaldoCard
          label="Recursos"
          monto={reportes.recursosYGastos?.recursos?.total ?? 0}
          color="text-emerald-700"
        />
        <SaldoCard
          label="Gastos"
          monto={reportes.recursosYGastos?.gastos?.total ?? 0}
          color="text-orange-700"
        />
        <SaldoCard
          label="Resultado del ejercicio"
          monto={reportes.recursosYGastos?.resultadoEjercicio ?? 0}
          color={(reportes.recursosYGastos?.resultadoEjercicio ?? 0) >= 0 ? 'text-green-700' : 'text-red-700'}
        />
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-700">Activo = Pasivo + Patrimonio Neto + Resultado del Ejercicio</span>
          <span className={`font-semibold ${reportes.situacionPatrimonial?.balanceado ? 'text-green-700' : 'text-red-700'}`}>
            {reportes.situacionPatrimonial?.balanceado ? '✓ Balanceado' : '✗ No balanceado'}
          </span>
        </div>
      </div>
    </div>
  );
}

function SaldoCard({ label, monto, color }: { label: string; monto: number; color: string }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-3">
      <div className="text-xs text-gray-500 uppercase mb-1">{label}</div>
      <div className={`text-base font-bold ${color}`}>$ {formatImporte(monto)}</div>
    </div>
  );
}

// =============================================================================
// Vista Rendición Campaña
// =============================================================================

interface RendicionCampanaFull extends RendicionLite {
  tipoEleccion: string;
  etapa: string;
  fechaActoElectoral: string;
  expedienteId: string | null;
}

function ResumenCampana({ rendicionId, partidoId }: { rendicionId: string; partidoId: string }) {
  const { data: rendicion, isLoading } = useQuery({
    queryKey: ['rendicion-campana-resumen', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.getById(rendicionId);
      return res.data as RendicionCampanaFull;
    },
  });

  const { data: validacionesData } = useQuery({
    queryKey: ['rendicion-campana-validaciones-resumen', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.getValidaciones(rendicionId);
      return res.data as { validaciones: ValidacionItem[] };
    },
    enabled: rendicion?.tipoPresentacion === 'COMPLETA',
  });

  const { data: reportes } = useQuery({
    queryKey: ['rendicion-campana-reportes-resumen', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.getReportes(rendicionId);
      return res.data as any;
    },
    enabled: rendicion?.tipoPresentacion === 'COMPLETA',
  });

  if (isLoading || !rendicion) {
    return <div className="bg-white rounded-lg shadow p-6 text-sm text-gray-500">Cargando rendición...</div>;
  }

  const esCopia = rendicion.tipoPresentacion === 'COPIA_DISTRITO';

  const labelEleccion =
    {
      LEGISLATIVA_PROVINCIAL: 'Legislativas Provinciales',
      EJECUTIVA_PROVINCIAL: 'Ejecutivas Provinciales',
      MUNICIPAL: 'Municipales',
      CONSEJO_MAGISTRATURA: 'Consejo de la Magistratura',
    }[rendicion.tipoEleccion] ?? rendicion.tipoEleccion;
  const labelEtapa =
    { PRIMARIA: 'Primaria', GENERAL: 'General', SEGUNDA_VUELTA: 'Segunda Vuelta' }[rendicion.etapa] ??
    rendicion.etapa;

  return (
    <div className="space-y-4">
      <HeaderRendicion
        rendicion={rendicion}
        subtitulo={`Rendición Campaña — ${labelEleccion} (${labelEtapa}) · ${formatFecha(
          rendicion.fechaActoElectoral,
        )}`}
        wizardLink={`/financiamiento/rendicion-campana/${partidoId}?rendicion=${rendicionId}`}
      />

      {!esCopia && (
        <>
          <ValidacionesAutomaticas validaciones={validacionesData?.validaciones} loading={!validacionesData} />
          {reportes && <ResumenContableCampana reportes={reportes} />}
          {reportes?.aportes && <ListadoAportantes aportes={reportes.aportes ?? []} />}
          {reportes?.candidaturas && reportes.candidaturas.length > 0 && (
            <ListadoCandidaturas candidaturas={reportes.candidaturas} />
          )}
        </>
      )}

      <ChecklistManual
        items={
          esCopia
            ? [
                {
                  texto: 'Verificar que el PDF de copia SPECA esté completo y legible',
                  detalle: 'Sin páginas faltantes, escaneo claro, todas las firmas visibles.',
                },
                {
                  texto: 'Verificar que el código SPECA del Paso 1 coincida con el PDF',
                  detalle: 'El código consignado en la cabecera del PDF debe coincidir con el cargado.',
                },
              ]
            : [
                {
                  texto: 'PDF firmado por contador + presidente + tesorero',
                  detalle: 'Las tres firmas deben estar presentes y legibles.',
                },
                {
                  texto: 'Aportes privados razonables (montos, frecuencia, aportantes)',
                  detalle: 'Verificar que no haya picos sospechosos.',
                },
                {
                  texto: 'Categorías y candidaturas coinciden con las oficializadas',
                  detalle: 'Cruzar con el listado oficial de candidaturas habilitadas para esa elección.',
                },
              ]
        }
      />
    </div>
  );
}

function ResumenContableCampana({ reportes }: { reportes: any }) {
  const ingresosTotal = reportes.ingresos?.total ?? 0;
  const egresosTotal = reportes.egresos?.total ?? 0;
  const resultado = reportes.resultado ?? 0;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
        Resumen de campaña
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <SaldoCard label="Ingresos totales" monto={ingresosTotal} color="text-emerald-700" />
        <SaldoCard label="Egresos totales" monto={egresosTotal} color="text-orange-700" />
        <SaldoCard
          label="Resultado de campaña"
          monto={resultado}
          color={resultado >= 0 ? 'text-green-700' : 'text-red-700'}
        />
      </div>
      {reportes.totalesAportes && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500">Personas humanas (dinero)</div>
            <div className="font-semibold">$ {formatImporte(reportes.totalesAportes.humanasDinero)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Personas humanas (especie)</div>
            <div className="font-semibold">$ {formatImporte(reportes.totalesAportes.humanasEspecie)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Personas jurídicas (dinero)</div>
            <div className="font-semibold">$ {formatImporte(reportes.totalesAportes.juridicasDinero)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Personas jurídicas (especie)</div>
            <div className="font-semibold">$ {formatImporte(reportes.totalesAportes.juridicasEspecie)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function ListadoCandidaturas({ candidaturas }: { candidaturas: Array<{ categoria: string; detalle: string | null }> }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
        Candidaturas <span className="text-xs text-gray-500 font-normal">({candidaturas.length})</span>
      </h3>
      <ul className="space-y-1 text-sm">
        {candidaturas.map((c, i) => (
          <li key={i} className="flex items-baseline gap-2">
            <span className="font-semibold text-gray-900">{c.categoria}</span>
            {c.detalle && <span className="text-gray-600">— {c.detalle}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
